import "./HomePage.css";
import SearchBar from "./SearchBar";
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { customFetch } from "../Utils/CustomFetch";
import SearchBarResults from "./SearchBarResults";
import debounce from "lodash/debounce";

function HomePage() {
    const [searchParams, setSearchParams] = useSearchParams();

    // State for data fetched from the server
    const [allJournals, setAllJournals] = useState([]);
    const [allArticles, setAllArticles] = useState([]);
    const [filters, setFilters] = useState([]);
    const [filterToCategory, setFilterToCategory] = useState({});

    // State for the results of the search
    const [filteredJournals, setFilteredJournals] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [filteredJournalCount, setFilteredJournalCount] = useState(0);
    const [filteredArticleCount, setFilteredArticleCount] = useState(0);

    // State for loading indicators
    const [loadingJournalData, setLoadingJournalData] = useState(true);
    const [loadingSearchResults, setLoadingSearchResults] = useState(false);

    // --- Derived values from URL ---
    // The URL is the single source of truth.
    // We use useMemo to stabilize these values and prevent unnecessary re-renders.
    const searchInput = searchParams.get("q") || "";
    const activeFilters = useMemo(() =>
        (searchParams.get("filters") || "").split(",").filter(Boolean).map(Number),
        [searchParams]
    );
    const activeFilterCategories = useMemo(() =>
        [...new Set(activeFilters.map(id => filterToCategory[id]).filter(Boolean))],
        [activeFilters, filterToCategory]
    );

    // --- Handlers to update the URL ---
    const handleSearchChange = (newSearchInput) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (newSearchInput) {
            newSearchParams.set("q", newSearchInput);
        } else {
            newSearchParams.delete("q");
        }
        setSearchParams(newSearchParams, { replace: true });
    };

    const handleFilterChange = (newActiveFilters) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (newActiveFilters.length > 0) {
            newSearchParams.set("filters", newActiveFilters.join(","));
        } else {
            newSearchParams.delete("filters");
        }
        setSearchParams(newSearchParams, { replace: true });
    };

    // --- Effect for fetching initial data ---
    useEffect(() => {
        customFetch("/search_bar_data", {})
            .then((res) => res.json())
            .then((result) => {
                setAllJournals(result.journals);
                const preprocessedArticles = result.articles.map(article => ({
                    ...article,
                    tags: new Set(article.tags.split("|").map(str => parseInt(str)))
                }));
                setAllArticles(preprocessedArticles);
                setFilteredJournals(result.journals);
                setLoadingJournalData(false);
            });

        customFetch("/all_filters", {})
         .then((res) => res.json())
         .then((result) => {
            const newFilters = result.map(filterGroup => ({
                label: filterGroup.label,
                showAll: false,
                options: filterGroup.options
            }));
            
            const newFilterToCategory = {};
            result.forEach(filterGroup => {
                filterGroup.options.forEach(option => {
                    newFilterToCategory[option.id] = filterGroup.label;
                });
            });
    
            setFilters(newFilters);
            setFilterToCategory(newFilterToCategory);
         });
    }, []); // Runs only once on component mount

    // --- Effect for running the search logic ---
    useEffect(() => {
        if (!allArticles.length && !allJournals.length) {
            return;
        }

        const runSearch = (query, currentFilters) => {
            const lowerCaseSearchInput = query.toLowerCase();

            const getFilteredArticles = () => {
                return allArticles.filter(article => {
                    if (!article.search_text.includes(lowerCaseSearchInput)) {
                        return false;
                    }

                    return activeFilterCategories.every(category => {
                        const categoryTagsInFilter = currentFilters.filter(filterId => filterToCategory[filterId] === category);
                        return categoryTagsInFilter.some(tagId => article.tags.has(tagId));
                    });
                });
            };

            const getFilteredJournals = () => {
                if (currentFilters.length > 0) return []; // Don't show journals if filters are active
                return allJournals.filter(journal =>
                    journal.search_text.toLowerCase().includes(lowerCaseSearchInput)
                );
            };

            if (query === "" && !currentFilters.length) {
                setFilteredJournals(allJournals);
                setFilteredArticles([]);
                setFilteredJournalCount(0);
                setFilteredArticleCount(0);
                setLoadingSearchResults(false);
                return;
            }

            const newFilteredJournals = getFilteredJournals();
            const newFilteredArticles = getFilteredArticles();

            newFilteredArticles.sort((a, b) => {
                const aIndex = a.search_text.indexOf(lowerCaseSearchInput);
                const bIndex = b.search_text.indexOf(lowerCaseSearchInput);
                return aIndex - bIndex;
            });

            setFilteredJournals(newFilteredJournals);
            setFilteredArticles(newFilteredArticles);
            setFilteredJournalCount(newFilteredJournals.length);
            setFilteredArticleCount(newFilteredArticles.length);
            setLoadingSearchResults(false);
        };

        const debouncedRunSearch = debounce(runSearch, 300);
        setLoadingSearchResults(true);
        debouncedRunSearch(searchInput, activeFilters);

        return () => debouncedRunSearch.cancel();
    }, [searchInput, activeFilters, allArticles, allJournals, activeFilterCategories, filterToCategory]);

    return (
        <div className="home-page">
            <div className="home-page-bottom" id="search-area">
                <SearchBar
                    searchInput={searchInput}
                    activeFilters={activeFilters}
                    onSearchChange={handleSearchChange}
                    onFilterChange={handleFilterChange}
                    filters={filters}
                    setFilters={setFilters}
                    dropdown={false}
                    placeholder={"SEARCH FOR JOURNALS, ARTICLES, TOPICS"}
                />

                <SearchBarResults
                    filteredArticles={filteredArticles}
                    filteredJournals={filteredJournals}
                    filteredJournalCount={filteredJournalCount}
                    filteredArticleCount={filteredArticleCount}
                    loadingJournalData={loadingJournalData}
                    homePage={true}
                    loadingSearchResults={loadingSearchResults}
                />
            </div>
        </div>
    );
}

export default HomePage;